import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { KanbanSquare } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { PageContainer } from '../../../common/components/PageContainer';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { useSupportAdminApi } from '../../../common/hooks/use-support-api.hook';
import type {
  InboxCounts,
  StaffConversation,
  StaffConversationDetail,
  SupportMessage,
  SupportTask,
} from '../../../common/models/support.model';
import { onRealtime } from '../../../common/services/realtime.service';
import { InboxList, type InboxFilter } from '../../../modules/support/staff/InboxList';
import { StaffThread } from '../../../modules/support/staff/StaffThread';
import { ConversationDetails } from '../../../modules/support/staff/ConversationDetails';
import { TaskDialog, draftFromTask } from '../../../modules/support/staff/TaskDialog';
import { useStaffSupport } from '../../../modules/support/staff/staff-support.store';

const CATEGORY_TO_TASK = { bug: 'bug', feature: 'feature', question: 'issue', billing: 'task', other: 'issue' } as const;

/** Ardealy's support inbox: every vendor's conversations, a reply box with internal notes, and the vendor at a glance. */
export const SupportInboxPage = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const api = useSupportAdminApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('c') || undefined;

  const [filter, setFilter] = useState<InboxFilter>('open');
  const [search, setSearch] = useState('');
  const [assignee, setAssignee] = useState('');
  const [items, setItems] = useState<StaffConversation[]>([]);
  const [counts, setCounts] = useState<InboxCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<StaffConversationDetail | null>(null);
  const [allTasks, setAllTasks] = useState<SupportTask[]>([]);
  const [raising, setRaising] = useState(false);
  // Loaders must not depend on `t`: a new `t` would re-run them, and reloading
  // the thread clears it (and whatever arrived live) each time.
  const tRef = useRef(t);
  tRef.current = t;

  // ── Loading ────────────────────────────────────────────────────────────

  const loadList = useCallback(async () => {
    try {
      const res = await apiRef.current.listConversations({ status: filter, q: search || undefined, assignee: assignee || undefined });
      if (!res) return;
      setItems(res.items);
      setCounts(res.counts);
      useStaffSupport.getState().setCounts(res.counts);
    } catch (e: any) {
      toast.error(e?.message || tRef.current('SUPPORT.STAFF.LOAD_FAILED'));
    } finally {
      setLoading(false);
    }
  }, [filter, search, assignee]);

  useEffect(() => {
    const id = setTimeout(loadList, search ? 250 : 0);
    return () => clearTimeout(id);
  }, [loadList, search]);

  const loadDetail = useCallback(async (id: string) => {
    try {
      const d = await apiRef.current.getConversation(id);
      if (d) setDetail(d);
    } catch (e: any) {
      toast.error(e?.message || tRef.current('SUPPORT.STAFF.LOAD_FAILED'));
    }
  }, []);

  useEffect(() => {
    setDetail(null);
    if (!selectedId) return;
    loadDetail(selectedId);
    apiRef.current.markRead(selectedId).catch(() => undefined);
  }, [selectedId, loadDetail]);

  useEffect(() => {
    apiRef.current.listTasks().then((list) => list && setAllTasks(list)).catch(() => undefined);
  }, []);

  // ── Live ───────────────────────────────────────────────────────────────

  const selectedRef = useRef(selectedId);
  selectedRef.current = selectedId;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const offs = [
      onRealtime('support:conversation', (conv: StaffConversation) => {
        if (!conv?._id) return;
        setItems((list) => (list.some((c) => c._id === conv._id) ? list.map((c) => (c._id === conv._id ? conv : c)) : list));
        setDetail((d) => (d && d.conversation._id === conv._id ? { ...d, conversation: conv } : d));
        // A new or moved conversation may enter or leave the current filter.
        clearTimeout(timer);
        timer = setTimeout(loadList, 600);
      }),
      onRealtime('support:message', ({ conversationId, message }: { conversationId: string; message: SupportMessage }) => {
        if (conversationId !== selectedRef.current || !message) return;
        setDetail((d) => (d && !d.messages.some((m) => m._id === message._id) ? { ...d, messages: [...d.messages.filter((m) => !m.pending), message] } : d));
        if (message.authorKind === 'vendor' && document.visibilityState === 'visible') {
          apiRef.current.markRead(conversationId).catch(() => undefined);
        }
      }),
      onRealtime('support:task', (task: SupportTask) => {
        setAllTasks((list) => [task, ...list.filter((x) => x._id !== task._id)]);
        setDetail((d) => (d && task.conversations?.includes(d.conversation._id) ? { ...d, tasks: [...d.tasks.filter((x) => x._id !== task._id), task] } : d));
      }),
      onRealtime('support:task_removed', ({ _id }: { _id: string }) => {
        setAllTasks((list) => list.filter((x) => x._id !== _id));
        setDetail((d) => (d ? { ...d, tasks: d.tasks.filter((x) => x._id !== _id) } : d));
      }),
    ];
    return () => {
      clearTimeout(timer);
      offs.forEach((off) => off());
    };
  }, [loadList]);

  // ── Actions ────────────────────────────────────────────────────────────

  const select = (id: string) => {
    params.set('c', id);
    setParams(params, { replace: false });
  };

  const upload = useCallback((file: File, onProgress: (f: number) => void) => apiRef.current.upload(file, onProgress), []);

  // By the selected id, not the loaded detail: the reply box shows (from the
  // list row) before the thread has loaded, and a reply then must not be lost.
  const reply = async (p: { body: string; attachments: any[]; internal: boolean }) => {
    const id = selectedId;
    if (!id) throw new Error(t('SUPPORT.STAFF.PICK'));
    const saved = await apiRef.current.reply(id, p);
    if (saved) setDetail((d) => (d && d.conversation._id === id && !d.messages.some((m) => m._id === saved._id) ? { ...d, messages: [...d.messages, saved] } : d));
  };

  const update = async (patch: Partial<Pick<StaffConversation, 'status' | 'priority' | 'assignee'>>) => {
    if (!detail) return;
    try {
      const updated = await apiRef.current.updateConversation(detail.conversation._id, patch);
      if (updated) {
        setDetail((d) => (d ? { ...d, conversation: updated } : d));
        setItems((list) => list.map((c) => (c._id === updated._id ? updated : c)));
      }
      // Resolving adds a line to the timeline.
      if (patch.status) loadDetail(detail.conversation._id);
    } catch (e: any) {
      toast.error(e?.message || t('SUPPORT.STAFF.ACTION_FAILED'));
    }
  };

  const linkTask = async (taskId: string) => {
    if (!detail) return;
    try {
      await apiRef.current.linkTask(taskId, detail.conversation._id);
      await loadDetail(detail.conversation._id);
    } catch (e: any) {
      toast.error(e?.message || t('SUPPORT.STAFF.ACTION_FAILED'));
    }
  };

  const firstVendorMessage = useMemo(() => detail?.messages.find((m) => m.authorKind === 'vendor'), [detail]);

  // Replying moves a conversation from Open to Waiting; it stays in the list
  // while it is the one on screen, instead of vanishing mid-conversation.
  const listed = useMemo(
    () => (detail && selectedId && !items.some((c) => c._id === selectedId) ? [detail.conversation, ...items] : items),
    [detail, selectedId, items],
  );

  return (
    <PageContainer
      breadcrumbs={[{ label: t('SUPPORT.STAFF.SECTION') }, { label: t('SUPPORT.STAFF.INBOX') }]}
      status={
        <Button asChild variant="outline" size="sm">
          <Link to="/support/board"><KanbanSquare data-icon="inline-start" />{t('SUPPORT.STAFF.BOARD')}</Link>
        </Button>
      }
    >
      <div
        className="grid h-[calc(100vh-8.5rem)] min-h-[560px] overflow-hidden rounded-2xl border bg-card md:grid-cols-[320px_1fr] xl:grid-cols-[320px_1fr_300px]"
        data-testid="support-inbox"
      >
        <div className="flex min-h-0 flex-col border-e">
          <InboxList
            filter={filter}
            onFilter={(f) => { setLoading(true); setFilter(f); }}
            counts={counts}
            search={search}
            onSearch={setSearch}
            assignee={assignee}
            onAssignee={(a) => { setLoading(true); setAssignee(a); }}
            me={user?.email}
            items={listed}
            loading={loading}
            selectedId={selectedId}
            onSelect={select}
          />
        </div>
        <div className="flex min-h-0 min-w-0 flex-col">
          <StaffThread
            conversation={detail?.conversation ?? items.find((c) => c._id === selectedId)}
            messages={detail?.messages}
            me={user?.email}
            upload={upload}
            onReply={reply}
            onUpdate={update}
          />
        </div>
        <div className="hidden min-h-0 flex-col border-s xl:flex">
          {detail ? (
            <ConversationDetails
              detail={detail}
              allTasks={allTasks}
              onRaiseTask={() => setRaising(true)}
              onLinkTask={linkTask}
              onOpenConversation={select}
            />
          ) : null}
        </div>
      </div>

      {detail && (
        <TaskDialog
          open={raising}
          onOpenChange={setRaising}
          title={t('SUPPORT.STAFF.RAISE_TASK')}
          description={t('SUPPORT.STAFF.RAISE_TASK_DESC', { vendor: detail.conversation.vendorName })}
          linkedToVendor
          me={user?.email}
          initial={draftFromTask({
            type: CATEGORY_TO_TASK[detail.conversation.category] || 'issue',
            title: detail.conversation.subject,
            description: firstVendorMessage?.body || '',
            priority: detail.conversation.priority === 'urgent' ? 'urgent' : detail.conversation.priority === 'high' ? 'high' : 'medium',
            assignee: detail.conversation.assignee || user?.email,
          })}
          onSubmit={async (payload) => {
            await apiRef.current.createTask({ ...payload, conversationId: detail.conversation._id });
            await loadDetail(detail.conversation._id);
            toast.success(t('SUPPORT.STAFF.TASK_CREATED'));
          }}
        />
      )}
    </PageContainer>
  );
};

export default SupportInboxPage;
